import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  MapPin, 
  User, 
  Clock, 
  Sparkles, 
  ArrowRight, 
  Save,
  Loader2,
  Info,
  AlertTriangle,
  CheckCircle,
  Star
} from 'lucide-react';
import { useFirebase } from '../../context/firebaseContext';
import { useAuth } from '../../context/authContext';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import TransitForm from './TransitForm';
import TransitDisplay from './TransitDisplay';
import '../../assets/transit-dashboard.sass';

const TransitDashboard = ({ isMobile }) => {
  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    latitude: '',
    longitude: '',
    timeZone: ''
  });
  const [transitData, setTransitData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedBirthData, setSavedBirthData] = useState([]);
  const [selectedSavedData, setSelectedSavedData] = useState(null);
  const { db } = useFirebase();
  const { user } = useAuth();

  useEffect(() => {
    let isMounted = true;
    const loadBirthData = async () => {
      if (!user?.uid) {
        setSavedBirthData([]);
        return;
      }
      try {
        const ref = collection(db, 'users', user.uid, 'birthData');
        const snapshot = await getDocs(ref);
        if (!isMounted) return;
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSavedBirthData(items);
      } catch (err) {
        console.error('Error loading saved birth data:', err);
      }
    };
    loadBirthData();
    return () => { isMounted = false; };
  }, [db, user]);

  const handleFormSubmit = async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call for transit calculation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Placeholder transit data - replace with actual astrological calculations
      const mockTransitData = {
        currentTime: new Date().toISOString(),
        birthInfo: data,
        transits: [
          {
            planet: 'Sun',
            sign: 'Libra',
            degree: '15° 23\'',
            aspect: 'Conjunction',
            natalPlanet: 'Venus',
            natalSign: 'Libra',
            natalDegree: '14° 45\'',
            orb: '0° 38\'',
            influence: 'Harmonious',
            description: 'The Sun is conjunct your natal Venus, bringing love, beauty, and harmony to the forefront. This is an excellent time for relationships and creative pursuits.',
            intensity: 'high',
            duration: '1-2 days'
          },
          {
            planet: 'Mercury',
            sign: 'Virgo',
            degree: '28° 12\'',
            aspect: 'Trine',
            natalPlanet: 'Mars',
            natalSign: 'Capricorn',
            natalDegree: '27° 33\'',
            orb: '0° 39\'',
            influence: 'Harmonious',
            description: 'Mercury trines your natal Mars, enhancing communication and mental energy. Great time for planning and taking action on your goals.',
            intensity: 'medium',
            duration: '2-3 days'
          },
          {
            planet: 'Venus',
            sign: 'Leo',
            degree: '5° 47\'',
            aspect: 'Square',
            natalPlanet: 'Saturn',
            natalSign: 'Scorpio',
            natalDegree: '6° 22\'',
            orb: '0° 35\'',
            influence: 'Challenging',
            description: 'Venus squares your natal Saturn, creating tension in relationships and self-worth. Focus on patience and realistic expectations.',
            intensity: 'medium',
            duration: '1-2 days'
          }
        ],
        majorTransits: [
          {
            planet: 'Jupiter',
            sign: 'Taurus',
            degree: '12° 15\'',
            aspect: 'Conjunction',
            natalPlanet: 'Uranus',
            natalSign: 'Taurus',
            natalDegree: '11° 48\'',
            orb: '0° 27\'',
            influence: 'Expansive',
            description: 'Jupiter conjunct your natal Uranus brings unexpected opportunities and breakthroughs. Embrace change and innovation.',
            intensity: 'very high',
            duration: '1-2 weeks'
          }
        ]
      };
      
      setTransitData(mockTransitData);
    } catch (err) {
      setError('Failed to calculate transits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSavedData = (savedData) => {
    setFormData(savedData);
    setSelectedSavedData(savedData);
  };

  const handleSaveBirthData = (data) => {
    const birthDataToSave = {
      id: Date.now(),
      ...data,
      savedAt: new Date().toISOString()
    };
    
    setSavedBirthData(prev => [...prev, birthDataToSave]);

    if (!user?.uid) {
      return;
    }

    const persist = async () => {
      try {
        const ref = collection(db, 'users', user.uid, 'birthData');
        const docRef = await addDoc(ref, { ...data, savedAt: serverTimestamp() });
        setSavedBirthData(prev => [
          ...prev.filter(item => item.id !== birthDataToSave.id),
          { ...birthDataToSave, id: docRef.id }
        ]);
      } catch (err) {
        console.error('Error saving birth data:', err);
      }
    };

    persist();
  };

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{ 
        backgroundImage: `url('/img/saturnwallpaper.jpg')`,
        minHeight: '100vh',
        padding: isMobile ? '1rem' : '2rem'
      }}
    >
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="transit-dashboard"
        >
          {/* Header */}
          
           

          {/* Main Content */}
          <AnimatePresence mode="wait">
            {!transitData ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.4 }}
              >
                <TransitForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleFormSubmit}
                  onLoadSavedData={handleLoadSavedData}
                  onSaveBirthData={handleSaveBirthData}
                  savedBirthData={savedBirthData}
                  selectedSavedData={selectedSavedData}
                  loading={loading}
                  isMobile={isMobile}
                />
              </motion.div>
            ) : (
              <motion.div
                key="display"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
              >
                <TransitDisplay
                  transitData={transitData}
                  onBack={() => setTransitData(null)}
                  isMobile={isMobile}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'rgba(220, 53, 69, 0.2)',
                border: '1px solid rgba(220, 53, 69, 0.5)',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <AlertTriangle size={20} color="#dc3545" />
              <span style={{ color: '#dc3545' }}>{error}</span>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TransitDashboard;
